import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";

import { ApiError } from "@config/apiClient";
import { queryClient } from "@config/queryClient";
import useEmailStore from "@store/useEmailStore";
import emailService from "@services/email.service";
import useSystemStore from "@store/useSystemStore";
import usePlatformStore from "@store/usePlatformStore";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS, ERROR_MESSAGES } from "@config/messages";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { DisconnectedError, RequestAbortedError } from "@app-types/errors";
import { EMAIL_QUERY_KEY, SYNC_EMAIL_MUTATION_KEY, THREAD_QUERY_KEY } from "@config/query";
import { ActiveScreen } from "@enums/screens.enum";
import { SyncEmailRequest } from "@app-types/requests.type";
import { retryWithBackoff } from "@utils/retry.util";

// Retry attempts needed for Classic Outlook for Windows:
// - in compose mode: retry multiple times with exponential backoff
// - in read mode: retry 1 more time (as I've seen transient failures when syncing an email that was just sent)
const MAX_COMPOSE_MODE_RETRY_ATTEMPTS = 7;
const MAX_READ_MODE_RETRY_ATTEMPTS = 2;
const INITIAL_RETRY_DELAY_MS = 1000;

const SYNC_DISABLED_ERROR_CODES = ["meridian_inbox_message", "meridian_platform_email"];

export function isSyncDisabledError(error: unknown): boolean {
  const apiError = error as ApiError;
  const code = (apiError?.response?.data as { code?: string })?.code;
  return !!code && SYNC_DISABLED_ERROR_CODES.includes(code);
}

export function useSyncEmail(onError?: () => void) {
  const { isComposeMode } = usePlatformStore();
  const { notifyBugsnag } = useBugsnagErrorHandler();
  const { changeActiveScreen } = useSystemStore();
  const { setEmailId, setThreadId } = useEmailStore();
  const abortControllerRef = useRef<AbortController | null>(null);

  return useMutation({
    mutationKey: [SYNC_EMAIL_MUTATION_KEY],
    mutationFn: async (data: SyncEmailRequest) => {
      // Cancel a previous in-flight subscribe so rapid item switches don't pile up.
      // Safe: BE syncs via Nylas regardless, and subscribe is idempotent — once subscribed
      // it just returns the email, so dropping a superseded request loses nothing.
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        // The retry logic is needed for Classic Outlook for Windows,
        // since the saved draft may not appear in the Exchange mailbox immediately
        return await retryWithBackoff(
          async () => await emailService.syncEmail(data, controller.signal),
          isComposeMode ? MAX_COMPOSE_MODE_RETRY_ATTEMPTS : MAX_READ_MODE_RETRY_ATTEMPTS,
          INITIAL_RETRY_DELAY_MS,
          { shouldRetry: (error) => error?.response?.status === 400, signal: controller.signal }
        );
      } catch (error) {
        if (controller.signal.aborted) throw new RequestAbortedError();
        throw error;
      }
    },
    onSuccess: (response, data) => {
      // Drop a stale subscribe whose item is no longer selected (live itemId, not the closure).
      if (data.identifier !== usePlatformStore.getState().itemId) return;

      setEmailId(response.id ?? null);
      setThreadId(response.thread ?? null);

      changeActiveScreen(ActiveScreen.THREAD);

      queryClient.setQueryData([EMAIL_QUERY_KEY, data.identifier], response);
      void queryClient.invalidateQueries({ queryKey: [THREAD_QUERY_KEY, response.thread] });

      let headerText: string;

      if (isComposeMode) {
        headerText = data.sync_thread ? "Thread will sync when sent" : "Email will sync when sent";

        ToastService.showSuccess(headerText, { id: "sync-when-sent", duration: Infinity });
      } else {
        headerText = data.sync_thread
          ? "Thread subscribed to Meridian"
          : "Email synced to Meridian";

        ToastService.showSuccess(headerText);
      }
    },
    onError: (error) => {
      // Superseded by a newer subscribe — nothing to surface.
      if (error instanceof RequestAbortedError) return;

      if (error instanceof DisconnectedError) return;

      if (isSyncDisabledError(error)) {
        changeActiveScreen(ActiveScreen.SYNC_DISABLED);
        return;
      }

      notifyBugsnag(error, ERROR_STRINGS.SYNC_EMAIL);

      if (onError) {
        onError();
      } else {
        ToastService.showError(error as ApiError, ERROR_MESSAGES.SYNC_EMAIL);
      }
    },
  });
}
